import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSede, setFilterSede] = useState('');
  const [filterSpace, setFilterSpace] = useState('');
  const [sedes, setSedes] = useState([]); // For filter dropdown
  const [spaces, setSpaces] = useState([]); // For filter dropdown
  const [allServices, setAllServices] = useState([]); // To store all services

  useEffect(() => {
    const fetchBookingsAndMetadata = async () => {
      setLoading(true);
      try {
        // Fetch bookings
        const bookingsQuery = query(collection(db, 'eventBookings'), orderBy('createdAt', 'desc'));
        const bookingsSnapshot = await getDocs(bookingsQuery);
        let fetchedBookings = bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Fetch sedes and spaces for display and filtering
        const eventSpacesQuery = query(collection(db, 'eventSpaces'));
        const eventSpacesSnapshot = await getDocs(eventSpacesQuery);
        const allEventSpaces = eventSpacesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const fetchedSedes = allEventSpaces.filter(space => space.type === 'sede');
        const fetchedSpaces = allEventSpaces.filter(space => space.type === 'eventSpace');

        setSedes(fetchedSedes);
        setSpaces(fetchedSpaces);

        // Fetch all services
        const servicesSnapshot = await getDocs(collection(db, 'services'));
        const fetchedServices = servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllServices(fetchedServices);

        // Enrich bookings with sede and space names
        fetchedBookings = fetchedBookings.map(booking => {
          const sede = fetchedSedes.find(s => s.id === booking.sedeId);
          const salon = fetchedSpaces.find(s => s.id === booking.salonId);
          return {
            ...booking,
            sedeTitle: sede ? sede.title : 'N/A',
            salonTitle: salon ? salon.title : 'N/A',
          };
        });

        setBookings(fetchedBookings);
      } catch (err) {
        setError('Error al cargar las cotizaciones: ' + err.message);
        console.error('Error fetching bookings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingsAndMetadata();
  }, []);

  const filteredBookings = bookings.filter(booking => {
    const matchesSearchTerm = searchTerm === '' ||
      booking.sedeTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.salonTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.comments.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (booking.userEmail && booking.userEmail.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (booking.createdAt?.toDate().toLocaleDateString() || '').includes(searchTerm.toLowerCase());

    const matchesFilterSede = filterSede === '' || booking.sedeId === filterSede;
    const matchesFilterSpace = filterSpace === '' || booking.salonId === filterSpace;

    return matchesSearchTerm && matchesFilterSede && matchesFilterSpace;
  });

  if (loading) {
    return <div style={{ padding: '20px' }}>Cargando cotizaciones...</div>;
  }

  if (error) {
    return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h1>Cotizaciones de Eventos</h1>

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <input
          type="text"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: '8px', width: '200px' }}
        />
        <select
          value={filterSede}
          onChange={(e) => setFilterSede(e.target.value)}
          style={{ padding: '8px' }}
        >
          <option value="">Todas las Sedes</option>
          {sedes.map(sede => (
            <option key={sede.id} value={sede.id}>{sede.title}</option>
          ))}
        </select>
        <select
          value={filterSpace}
          onChange={(e) => setFilterSpace(e.target.value)}
          style={{ padding: '8px' }}
        >
          <option value="">Todos los Espacios</option>
          {spaces.map(space => (
            <option key={space.id} value={space.id}>{space.title}</option>
          ))}
        </select>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        <thead>
          <tr style={{ backgroundColor: '#f0f0f0' }}>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Fecha</th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Sede</th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Espacio</th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Servicios</th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Comentarios</th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Nombre de Contacto</th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Teléfono de Contacto</th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Correo Electrónico</th>
          </tr>
        </thead>
        <tbody>
          {filteredBookings.length > 0 ? (
            filteredBookings.map(booking => (
              <tr key={booking.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>{booking.createdAt?.toDate().toLocaleDateString() || 'N/A'}</td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>{booking.sedeTitle}</td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>{booking.salonTitle}</td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {booking.services && booking.services.length > 0
                    ? booking.services.map((service, index) => (
                        <span key={`${service.id}-${index}`} style={{ display: 'block' }}>{service.name}</span>
                      ))
                    : 'Ninguno'}
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>{booking.comments || 'N/A'}</td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>{booking.userName || 'N/A'}</td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>{booking.userPhone || 'N/A'}</td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>{booking.userEmail || 'N/A'}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" style={{ padding: '12px', textAlign: 'center' }}>No se encontraron cotizaciones.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminBookings;